import { useEffect, useState } from "react";
import { Contract, ContractDeviationType, Image, Order, RegisterOrderDeviation } from "@/types";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DeviationFormInput, DeviationSchema } from "./validationSchema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postRegisterDeviation } from "@/services/Orders";
import { Grid } from "@mantine/core";
import { MIME_TYPES } from "@mantine/dropzone";
import { useGetContractById } from "@/services/Contracts";
import { useGetContractDeviationTypes, useGetContractDeviationUnits } from "@/services/ContractDeviations";
import { fileToBase64Raw, getFileTypeFromFile } from "@/utils/FileUtils";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

import Text from "@/components/atoms/Text";
import Select from "@/components/atoms/Select";
import CTA from "@/components/atoms/CTA";
import Group from "@/components/atoms/Group";
import Loader from "@/components/atoms/Loader";
import Textarea from "@/components/atoms/Textarea";
import Dropzone from "@/components/molecules/Dropzone";
import Accordion from "@/components/organisms/AccordionDrawer";
import TextInput from "@/components/atoms/TextInput";
import DeviationPriceUnitEnum from "@/constants/enums/DeviationPriceUnitEnum";
import styles from "./styles.module.css";

interface NewDeviationProps {
  order: Order;
}

const NewDeviation = ({ order }: NewDeviationProps) => {
  const [isCreatingDeviation, setIsCreatingDeviation] = useState(false);
  const { t } = useTranslation();

  if (!order.contractId) {
    return <>{t("contracts:contractDeviation.noContractAttachedToOrder")}</>;
  }

  const { data: contractData, isLoading: isContractDataLoading } = useGetContractById(order.contractId);

  if (isContractDataLoading) {
    return <Loader />;
  }
  if (!contractData) {
    return <Text>{t("contracts:contractDeviation.noValidContractAttachedToOrder")}</Text>;
  }

  return (
    <>
      {!isCreatingDeviation && <CreateNewDeviationCTA onClick={() => setIsCreatingDeviation(true)} />}
      {isCreatingDeviation && (
        <NewDeviationForm order={order} contractData={contractData} setIsCreatingDeviation={setIsCreatingDeviation} />
      )}
    </>
  );
};

export default NewDeviation;

const CreateNewDeviationCTA = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Group justify="center" className={styles.newClaimButton}>
      <CTA size="lg" intent="secondary" data-testid="new-deviation-btn" onClick={onClick}>
        {t("contracts:contractDeviation.newDeviation")}
      </CTA>
    </Group>
  );
};

interface NewDeviationFormProps {
  order: Order;
  contractData: Contract;
  setIsCreatingDeviation: (isCreating: boolean) => void;
}

const NewDeviationForm = ({ order, contractData, setIsCreatingDeviation }: NewDeviationFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [deviationOptions, setDeviationOptions] = useState<ContractDeviationType[]>([]);

  const { data: deviationUnits } = useGetContractDeviationUnits();
  const { data: contractDeviationsData, isLoading: iscontractDeviationsDataLoading } = useGetContractDeviationTypes(
    contractData.createdByCompany,
    false
  );

  useEffect(() => {
    if (contractDeviationsData && contractData) {
      const deviationsOnContract = contractDeviationsData.filter((du) =>
        contractData.contractDeviations.some((cd) => cd.contractDeviationTypeId === du.id)
      );

      setDeviationOptions(deviationsOnContract);
    }
  }, [contractDeviationsData, contractData]);

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<DeviationFormInput>({
    resolver: zodResolver(DeviationSchema),
  });

  const { mutateAsync: mutateRegisterDeviation } = useMutation({
    mutationFn: async (data: RegisterOrderDeviation) => {
      await postRegisterDeviation(order.id, data);
    },
    onSuccess: async () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:generic.createSuccess"),
          "order-deviation-created-success"
        )
      );
      await queryClient.invalidateQueries({
        queryKey: ["getDeviationsByOrderId"],
      });
      setIsCreatingDeviation(false);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:generic.createError"),
          "order-deviation-created-error"
        )
      );
    },
  });

  const onSubmit: SubmitHandler<DeviationFormInput> = async (data) => {
    if ((isAmountVisible() && !data.amount) || (data.amount && Number(data.amount) <= 0)) {
      setError("amount", {
        type: "required",
      });

      return;
    }

    const attachments = getValues("image");
    let base64Images: Image | undefined = undefined;
    if (attachments && attachments.length > 0) {
      const base64File = await fileToBase64Raw(attachments[0]);
      base64Images = {
        image: base64File,
        documentType: "IMAGE",
        fileType: getFileTypeFromFile(attachments[0]),
      };
    }

    await mutateRegisterDeviation({
      description: data.description,
      contractDeviationTypeId: data.contractDeviationTypeId,
      amount: data.amount ? Number(data.amount) : 0,
      image: base64Images ?? null,
    });
  };

  const isAmountVisible = () => {
    if (!watch("contractDeviationTypeId")) return false;
    const selectedDeviationTypeId = watch("contractDeviationTypeId");
    const selectedDeviation = deviationOptions.find((d) => d.id === Number(selectedDeviationTypeId));

    if (selectedDeviation?.priceUnit === DeviationPriceUnitEnum.PPO.id) return false;
    return true;
  };

  if (iscontractDeviationsDataLoading) {
    return <Loader />;
  }
  if (deviationOptions.length < 1) {
    return (
      <Group justify="center" p="lg">
        <Text>{`${t("contracts:contractDeviation.noDeviationsOnContract")}`}</Text>
      </Group>
    );
  }

  const getDeviationDropdownItemText = (option: ContractDeviationType) => {
    return `${option.name} - ${option.price} ${option.currency}/${t("enumerables:priceUnit." + deviationUnits?.find((du) => du.id === option.priceUnit)?.code)}`;
  };

  const amountLabel = () => {
    const _priceUnit = deviationOptions?.find((du) => du.id === watch("contractDeviationTypeId"))?.priceUnit;
    const _devUnits = deviationUnits?.find((du) => du.id === _priceUnit)?.code;

    const translatedUnit = t("contracts:contractDeviation.amountOfUnits." + _devUnits);
    return translatedUnit;
  };

  const onChangeDeviationType = () => {
    if (isAmountVisible()) {
      const orderAmount = order.amount ?? 0;
      setValue("amount", String(orderAmount));
    } else {
      setValue("amount", "");
    }
  };

  return (
    <Accordion title={t("contracts:contractDeviation.newDeviation")} initiallyOpen>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid p="lg">
          <Grid.Col span={7}>
            <Controller
              control={control}
              name="contractDeviationTypeId"
              render={({ field: { onChange, value } }) => (
                <Select
                  data-testid="deviation-type-select"
                  label={t("contracts:contractDeviation.deviation")}
                  withAsterisk
                  data={deviationOptions.map((option) => ({
                    label: getDeviationDropdownItemText(option),
                    value: String(option.id),
                  }))}
                  value={String(value)}
                  onChange={(e) => {
                    onChange(Number(e));
                    onChangeDeviationType();
                  }}
                  error={errors.contractDeviationTypeId ? t("validation:required") : null}
                />
              )}
            />
          </Grid.Col>

          {isAmountVisible() && (
            <Grid.Col span={7}>
              <Controller
                control={control}
                name="amount"
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    data-testid="deviation-amount-input"
                    label={amountLabel()}
                    value={value ?? ""}
                    withAsterisk
                    type="number"
                    onChange={(e) => onChange(e.target.value)}
                    error={errors.amount ? t("validation:required") : null}
                  />
                )}
              />
            </Grid.Col>
          )}

          <Grid.Col span={7}>
            <Controller
              control={control}
              name="description"
              render={({ field: { onChange, value } }) => (
                <Textarea
                  data-testid="deviation-description-input"
                  label={t("common:description")}
                  withAsterisk
                  value={value}
                  onChange={(e) => onChange(e)}
                  error={errors.description ? t("validation:required") : null}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={7}>
            <Controller
              control={control}
              name="image"
              render={({ field: { onChange, value = [] } }) => (
                <Dropzone
                  origin="local"
                  type={"button"}
                  files={value}
                  setFiles={onChange}
                  buttonPosition="left"
                  buttonLabel={t("actions:uploadImages")}
                  accept={[MIME_TYPES.jpeg, MIME_TYPES.png]}
                  maxNumberOfFiles={1}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={7}>
            <Text size="sm" color="tertiary">
              {t("contracts:contractDeviation.addingDeviationToActiveOrder")}
            </Text>
          </Grid.Col>
          <Grid.Col span={12}>
            <Group justify="end">
              <CTA intent="secondary" onClick={() => setIsCreatingDeviation(false)}>
                {t("actions:cancel")}
              </CTA>
              <CTA type="submit" data-testid="register-deviation-btn">
                {t("contracts:contractDeviation.registerDeviation")}
              </CTA>
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </Accordion>
  );
};
